import ApiService from '../common/api.service'

class ProductService extends ApiService {
  /**
   * Get number of products to update.
   * @param {String} vin VIN
   * @param {boolean} useCache Try to get categories from the cache
   * @return {Promise} number of products to update
   */
  getNumberProductToUpdate (vin, useCache = true) {
    this.$log.debug('getNumberProductToUpdate', vin)
    const cacheKey = `nb-product-${vin}`
    const inCache = this.getFromCache(cacheKey)
    if (useCache && inCache) {
      // Get product form cache
      return Promise.resolve(inCache)
    }
    return this.get('/products/update/count', {params: {vin}})
      .then((res) => {
        this.putInCache(cacheKey, res.data)
        this.$log.info('Fetched number of products to update', res.data)
        return Promise.resolve(res.data)
      })
  }

  /**
   * Get products to update.
   * @param {String} vin VIN
   * @return {Promise} products to update
   */
  getProductsToUpdate (vin) {
    return this.get('/products/update', {params: {vin}})
      .then((res) => {
        this.$log.info('Fetched products to updates', res.data)
        return Promise.resolve(res.data)
      })
  }

  /**
   * Ask to update products of a vehicle.
   * @param {String} vin VIN
   * @param {String} ref product reference
   * @return {Promise} update result
   */
  updateProducts (vin, ref) {
    return this.put('/products/update', null, {params: {vin, ref}})
      .then((res) => {
        this.$log.info('Update products requested', res.data)
        return Promise.resolve(res.data)
      })
  }

  /**
   * Get product categories.
   * @param {boolean} useCache Try to get categories from the cache
   * @return {Promise} Promise of the categories
   */
  getProductCategories (useCache = true) {
    const cacheKey = 'product_categories'
    const inCache = this.getFromCache(cacheKey)
    if (useCache && inCache) {
      // Get categories from cache
      return Promise.resolve(inCache)
    } else {
      // Get categories from the API
      return this.get('/products/categories')
      .then((res) => {
        this.putInCache('product_categories', res.data.categories)
        this.$log.info('Fetched product categories')
        return Promise.resolve(res.data.categories)
      })
    }
  }

  /**
   * Get products.
   * @param {object} params request params
   * @param {boolean} useCache Try to get products from the cache
   * @return {Promise} Promise of the products
   */
  getProducts (params, useCache = true) {
    this.$log.debug('getProducts', params)
    const inCache = this.getFromCache(params)
    if (useCache && inCache) {
      // Get products form cache
      return Promise.resolve(inCache)
    }
    // Get products from the API
    return this.get('/products', {params})
      .then((res) => {
        this.putInCache(params, res.data)
        this.$log.info('Fetched products', res.data)
        return Promise.resolve(res.data)
      })
  }

  /**
   * Get product.
   * @param {string} ref Product REF
   * @param {string} vin VIN
   * @param {boolean} useCache Try to get products from the cache
   * @return {Promise} Promise of the products
   */
  getProduct (ref, vin, locale, useCache = true) {
    const cacheKey = `product-${ref}-${vin}-${locale}`
    const inCache = this.getFromCache(cacheKey)
    if (useCache && inCache) {
      // Get product form cache
      return Promise.resolve(inCache)
    }
    // Get product from the API
    return this.get(`/products/${ref}`, {
      params: {
        'vin': vin
      }
    })
    .then((res) => {
      this.putInCache(cacheKey, res.data)
      this.$log.info('Fetched product')
      return Promise.resolve(res.data)
    })
  }

  /**
   * Get invoice download URL.
   * @param {String} productRef Product ref.
   * @param {String} vin VIN
   * @return {String} Invoice download URL
   */
  getProductInvoiceUrl (productRef, vin, token) {
    const country = this.defaultHeaders['X-Country']
    const locale = this.defaultHeaders['X-Locale']
    const tokenParam = token ? `&token=${token}` : ``
    return `${this.endpoint}/orders/${productRef}/invoice?vin=${vin}&x-locale=${locale}&x-country=${country}${tokenParam}`
  }

  getEndpoint () {
    return `${this.endpoint}`
  }

  getIsPreviouslyInstalled (productRef, vin) {
    return this.get(`/products/${productRef}/previous/installed`, {
      params: {
        'vin': vin
      }
    }).then((res) => Promise.resolve(res.data))
  }

  buyProduct (vin, productRef, purchaseInfos, voucherInfos) {
    return this.post(`/products/${productRef}/buy`, purchaseInfos.buyer, {
      params: {
        'vin': vin,
        'optIn': purchaseInfos.optIn,
        'cgv': true,
        'withdrawalRights': (purchaseInfos.withdrawalRights) ? purchaseInfos.withdrawalRights : false,
        'free': purchaseInfos.isFreeProduct,
        'paymentId': (purchaseInfos.paymentId) ? purchaseInfos.paymentId : null,
        'createToken': (purchaseInfos.createToken) ? purchaseInfos.createToken : false,
        'purchaseVoucher': voucherInfos.isPurchaseUsingVoucher,
        'voucherCode': (voucherInfos.voucherCode) ? voucherInfos.voucherCode : null,
        'amountReduction': (voucherInfos.amountReduction) ? parseFloat(voucherInfos.amountReduction).toFixed(2) : null,
        'channel': (voucherInfos.canalCode) ? voucherInfos.canalCode : null,
        'sessionId': (purchaseInfos.sessionId) ? purchaseInfos.sessionId : null,
        'paymentMethod': (purchaseInfos.paymentMethod) ? purchaseInfos.paymentMethod : null,
        'token': (purchaseInfos.token) ? purchaseInfos.token : null
      }
    }).then((res) => {
      return Promise.resolve(res.data)
    })
  }

  checkIfLatestMapIsInstallable (vin, musId) {
    return this.get(`/products/mus/${musId}/latest/map/installable`, {
      params: {
        'vin': vin
      }
    }).then((res) => Promise.resolve(res.data))
  }

  getB2BPrice (reference, isoCountryCode) {
    return this.get(`/products/${reference}/computeB2BPrice`, {
      params: {
        'isoCountryCode': isoCountryCode
      }
    })
    .then((res) => Promise.resolve(res.data))
  }

  checkIfMapIsInstallable (vin, reference) {
    return this.get(`/products/${reference}/installable`, {
      params: {
        'vin': vin
      }
    }).then((res) => Promise.resolve(res.data))
  }

  /*
  return this.post('/users/password', email, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  }).then((res) => {
    Promise.resolve(res.data)
  })
  */

}

export default ProductService



// WEBPACK FOOTER //
// ./src/app/services/product/product.service.js